'use client';

import { useMemo } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';
import { SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { getCourses } from '@/data/courses';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

interface FeaturedCoursesProps {
  searchQuery?: string;
}

// Elegant card style variations - refined and sophisticated
const CARD_STYLES = [
  {
    font: 'font-cormorant',
    gradient: 'from-amber-900/60',
    spot: 'bg-amber-500/60',
    spotPosition: 'bottom-[15%] -right-8',
    align: 'text-left',
  },
  {
    font: 'font-eb-garamond',
    gradient: 'from-rose-900/55',
    spot: 'bg-rose-500/55',
    spotPosition: 'bottom-[20%] -left-8',
    align: 'text-center',
  },
  {
    font: 'font-source-serif',
    gradient: 'from-emerald-900/55',
    spot: 'bg-emerald-500/55',
    spotPosition: 'bottom-[10%] -right-6',
    align: 'text-left',
  },
  {
    font: 'font-noto-serif',
    gradient: 'from-violet-900/50',
    spot: 'bg-violet-500/60',
    spotPosition: 'bottom-[15%] -left-10',
    align: 'text-center',
  },
  {
    font: 'font-cormorant',
    gradient: 'from-cyan-900/55',
    spot: 'bg-cyan-500/55',
    spotPosition: 'bottom-[20%] -right-8',
    align: 'text-left',
  },
  {
    font: 'font-eb-garamond',
    gradient: 'from-orange-900/55',
    spot: 'bg-orange-500/60',
    spotPosition: 'bottom-[10%] -left-6',
    align: 'text-center',
  },
  {
    font: 'font-source-serif',
    gradient: 'from-blue-900/50',
    spot: 'bg-blue-500/55',
    spotPosition: 'bottom-[15%] -right-10',
    align: 'text-left',
  },
  {
    font: 'font-noto-serif',
    gradient: 'from-pink-900/55',
    spot: 'bg-pink-500/60',
    spotPosition: 'bottom-[20%] -left-8',
    align: 'text-center',
  },
  {
    font: 'font-cormorant',
    gradient: 'from-teal-950/25',
    align: 'text-left',
  },
];

const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

const toArabicDigits = (value: number) =>
  String(value).replace(/\d/g, (d) => ARABIC_INDIC_DIGITS[Number(d)]);

/** Localized "N course(s)" count. The numeric VALUE never changes. */
function formatCourseCount(count: number, locale: string): string {
  if (locale !== 'ar') return `${count} course${count !== 1 ? 's' : ''}`;
  if (count === 1) return 'دورة واحدة';
  if (count === 2) return 'دورتان';
  const unit = count >= 3 && count <= 10 ? 'دورات' : 'دورة';
  return `${toArabicDigits(count)} ${unit}`;
}

/** Search-results summary line. Zero needs its own phrasing in Arabic. */
function formatResultsLabel(count: number, locale: string): string {
  if (locale !== 'ar') return `Found ${formatCourseCount(count, locale)}`;
  if (count === 0) return 'ما في نتائج مطابقة';
  return `وجدنا ${formatCourseCount(count, locale)}`;
}

export function FeaturedCourses({ searchQuery = '' }: FeaturedCoursesProps) {
  const { t, locale, isRTL } = useTranslation();
  const COURSES = getCourses(t.academy);

  // Elegant course display names
  const COURSE_DISPLAY = [
    {
      topLabel: t.academy['Original Series'],
      title: t.academy['AI Customer Support'],
      subtitle: t.academy['The Fundamentals'],
    },
    {
      topLabel: t.academy.New,
      title: t.academy.Conversational,
      subtitle: t.academy.Design,
    },
    {
      topLabel: t.academy.Masterclass,
      title: t.academy.Knowledge,
      subtitle: t.academy.Architecture,
    },
    {
      topLabel: t.academy.Advanced,
      title: t.academy.Agentic,
      subtitle: t.academy.Workflows,
    },
    {
      topLabel: t.academy['Original Series'],
      title: t.academy.Sentiment,
      subtitle: t.academy['& Empathy'],
    },
    {
      topLabel: t.academy.New,
      title: t.academy['Quality Assurance'],
      subtitle: t.academy['for AI'],
    },
    {
      topLabel: t.academy.Advanced,
      // "Voice" + " AI" concatenates to a broken "الصوت AI" in Arabic; the
      // Arabic name for the discipline is a single noun phrase.
      title:
        locale === 'ar' ? 'الذكاء الاصطناعي الصوتي' : `${t.academy.Voice} AI`,
      subtitle: t.academy.Implementation,
    },
    {
      topLabel: t.academy.Masterclass,
      title: t.academy.Multilingual,
      subtitle: t.academy.Strategy,
    },
    {
      topLabel: t.academy.Advanced,
      title: t.academy['AI Ethics'],
      subtitle: t.academy['& Compliance'],
    },
  ];

  // Row section titles for visual grouping
  const ROW_SECTIONS = [
    {
      title: t.academy['Start Here'],
      subtitle:
        t.academy['Essential foundations for AI-powered customer support'],
    },
    {
      title: t.academy['Level Up'],
      subtitle: t.academy['Advanced techniques and specialized skills'],
    },
    {
      title: t.academy['Expert Track'],
      subtitle: t.academy['Master the cutting edge of AI support'],
    },
  ];

  const filteredCourses = useMemo(() => {
    if (!searchQuery.trim()) return COURSES;

    const query = searchQuery.toLowerCase();
    return COURSES.filter(
      (course) =>
        course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query) ||
        course.level.toLowerCase().includes(query),
    );
  }, [searchQuery, COURSES]);

  // Calculate total lessons for each course
  const getTotalLessons = (course: (typeof COURSES)[0]) => {
    return course.modules.reduce(
      (total, module) => total + module.lessons.length,
      0,
    );
  };

  return (
    <section id="courses" className="bg-black py-24 text-white">
      <div className="container">
        <div className="mb-14 flex flex-col items-start gap-4">
          <div className="space-y-3">
            <h2 className="font-serif text-4xl text-white sm:text-5xl lg:text-6xl">
              {searchQuery
                ? locale === 'ar'
                  ? `نتائج البحث عن "${searchQuery}"`
                  : `Results for "${searchQuery}"`
                : ''}
            </h2>
            <p className="max-w-xl text-lg font-normal text-neutral-400">
              {searchQuery
                ? formatResultsLabel(filteredCourses.length, locale)
                : ''}
            </p>
          </div>
        </div>

        {filteredCourses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <SearchX className="mb-4 h-16 w-16 text-neutral-600" />
            <h3 className="mb-2 text-xl">{t.academy['No results found']}</h3>
            <p className="text-neutral-500">
              {
                t.academy[
                  'Try a different search term or browse all courses below.'
                ]
              }
            </p>
          </div>
        ) : (
          <div className="space-y-20">
            {/* Group courses into rows of 4 */}
            {Array.from({ length: Math.ceil(filteredCourses.length / 4) }).map(
              (_, rowIndex) => {
                const rowCourses = filteredCourses.slice(
                  rowIndex * 4,
                  rowIndex * 4 + 4,
                );
                const rowSection =
                  ROW_SECTIONS[rowIndex] ||
                  ROW_SECTIONS[ROW_SECTIONS.length - 1];

                return (
                  <div key={rowIndex} className="relative">
                    {/* Row Section Header */}
                    <div className="mb-8 flex items-end justify-between border-b border-white/10 pb-4">
                      <div>
                        <span className="mb-2 block text-[11px] font-medium tracking-[0.25em] text-white/40 uppercase">
                          {locale === 'ar'
                            ? `القسم ${toArabicDigits(rowIndex + 1)}`
                            : `Section ${rowIndex + 1}`}
                        </span>
                        <h3 className="text-2xl text-white">
                          {rowSection.title}
                        </h3>
                        <p className="mt-1 text-sm text-white/50">
                          {rowSection.subtitle}
                        </p>
                      </div>
                      <span className="text-sm text-white/30">
                        {formatCourseCount(rowCourses.length, locale)}
                      </span>
                    </div>

                    {/* Row Grid */}
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {rowCourses.map((course, courseIndex) => {
                        const globalIndex = rowIndex * 4 + courseIndex;
                        const style =
                          CARD_STYLES[globalIndex % CARD_STYLES.length];
                        const display =
                          COURSE_DISPLAY[globalIndex % COURSE_DISPLAY.length];
                        const isCentered = style.align === 'text-center';
                        const contentAlign = isCentered
                          ? 'text-center'
                          : isRTL
                            ? 'text-right'
                            : 'text-left';

                        return (
                          <motion.div
                            key={course.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              delay: courseIndex * 0.08,
                              duration: 0.5,
                            }}
                          >
                            <Link href={`/open-academy/courses/${course.slug}`}>
                              <div className="group relative aspect-[2/3.5] overflow-hidden rounded-lg bg-neutral-900">
                                {/* Background Image */}
                                <Image
                                  src={course.image}
                                  alt={course.title}
                                  fill
                                  className="object-cover transition-all duration-700 group-hover:scale-105 group-hover:brightness-110"
                                />

                                {/* Colorful spot accents - positioned at bottom */}
                                <div
                                  className={`absolute ${style.spotPosition} h-56 w-56 rounded-full ${style.spot} blur-2xl`}
                                />
                                <div
                                  className={`absolute bottom-[5%] left-1/4 h-40 w-40 rounded-full ${style.spot} opacity-50 blur-xl`}
                                />

                                {/* Gradient Overlays with stronger color tint */}
                                <div
                                  className={`absolute inset-0 bg-gradient-to-t ${style.gradient} via-black/60 to-transparent`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black from-0% via-black/80 via-35% to-transparent to-70%" />
                                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" />

                                {/* Top Badge - elegant styling */}
                                <div
                                  className={`absolute top-5 ${isCentered ? 'left-1/2 -translate-x-1/2' : isRTL ? 'right-5' : 'left-5'}`}
                                >
                                  <span className="rounded-sm bg-white/90 px-3 py-1 text-[10px] font-medium tracking-widest text-neutral-800">
                                    {display.topLabel}
                                  </span>
                                </div>

                                {/* Content - Bottom */}
                                <div
                                  className={`absolute inset-x-0 bottom-0 flex h-full flex-col justify-end p-6 ${contentAlign}`}
                                >
                                  {/* Category Label */}
                                  <span className="mb-3 text-[11px] font-normal tracking-[0.2em] text-white/60 uppercase">
                                    {t.academy['Open Academy']}
                                  </span>

                                  {/* Title - elegant, light weight */}
                                  <h3 className="font-serif mb-1 text-[1.6rem] text-white md:text-[1.85rem]">
                                    {display.title}
                                  </h3>
                                  <h4 className="font-serif mb-5 text-[1.15rem] text-white/85 md:text-[1.35rem]">
                                    {display.subtitle}
                                  </h4>

                                  {/* Lesson count */}
                                  <p className="text-[12px] font-normal tracking-wide text-white/50">
                                    {locale === 'ar'
                                      ? toArabicDigits(getTotalLessons(course))
                                      : getTotalLessons(course)}{' '}
                                    {t.academy.lessons} · {course.duration}
                                  </p>
                                </div>

                                {/* Subtle border overlay */}
                                <div className="absolute inset-0 rounded-lg ring-1 ring-white/5 transition-all duration-300 ring-inset group-hover:ring-white/15" />
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        )}

        <div className="mt-8 flex justify-center md:hidden">
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            {locale === 'ar' ? 'تصفّح كل الدورات' : 'View All Courses'}
          </Button>
        </div>
      </div>
    </section>
  );
}
